/**
 * Доля хвоста публичного рендерера: вид `phone_input` (поле телефона).
 *
 * WEB-2920, заход 4: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово. Метка `case 'phone_input'` остаётся в фасаде; ветка вида — функция
 * отрисовки `renderPhoneInput(ctx)`. Замыкания фасада приходят объектом-контекстом
 * (`ExtendedRenderContext`); ответ посетителя ветка читает из `values` и пишет
 * через `setValue` — это `useFormValues()` фасада: хук стоит до `switch`, в
 * функции отрисовки его звать нельзя (рисуется одна ветка за рендер). Эту
 * долю импортирует только фасад: она едет в ленивом куске `extended`, а не в
 * первом экране (самопроверка обхода).
 */
import React from 'react';
// WEB-1694: оформление текста ВНУТРИ поля ввода отдельно от подписи над ним
// (порядок силы слоёв — в inputFieldStyle.ts).
import {
  inputLabelStyleOverride,
  inputTextColorOverride,
  inputTextFontSizeOverride,
  // Слово владельца 21.10.2026: вид самого поля (фон, рамка, скругление,
  // высота, отступ, тень, подсказка, рамка в фокусе).
  inputBoxStyleOverride,
  inputLookClassName,
} from '../inputFieldStyle';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';

export function renderPhoneInput(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const { block, interp, values, setValue } = ctx;
  return (
    <div style={{ marginBottom: 16 }}>
      {block.content && (
        <label
          style={{
            display: 'block',
            fontWeight: 600,
            marginBottom: 8,
            color: 'var(--color-text-primary, inherit)',
            // WEB-1694: свой цвет и кегль ПОДПИСИ — накладкой ПОСЛЕ
            // строки выше. Пустые поля спредят {}: вид прежний до байта.
            ...inputLabelStyleOverride(block),
          }}
        >
          {interp(block.content)}
        </label>
      )}
      <input
        type="tel"
        // WEB-2923: подсказка автора — через дверь подстановки (`interp`).
        placeholder={
          block.placeholder == null
            ? '+1 (___) ___-____'
            : interp(block.placeholder)
        }
        required={block.required}
        // WEB-895 (возврат QA 2026-08-01): поле было НЕуправляемым — набранный
        // номер никуда не писался, и гейт обязательности видел пустоту всегда.
        // Пользователь с корректным номером оставался заперт навсегда.
        value={values[block.id] ?? ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setValue(block.id, e.target.value)
        }
        data-testid={`public-input-${block.id}`}
        className={inputLookClassName(block)}
        style={{
          width: '100%',
          padding: '10px 14px',
          border: '2px solid var(--color-border-primary, #d9d9d9)',
          borderRadius: 'var(--border-radius-md, 8px)',
          fontSize: 16,
          boxSizing: 'border-box',
          background: 'var(--color-bg-surface, #fff)',
          color:
            'var(--color-text-on-surface, var(--color-text-primary, inherit))',
          // WEB-1694: свой кегль и цвет ТЕКСТА ВНУТРИ поля — накладками
          // ПОСЛЕ зашитых строк выше. Пустые поля спредят {}.
          // Слово владельца 21.10.2026: вид САМОГО поля — накладкой ПОСЛЕ
          // зашитых строк выше; пусто — {}, решает стиль-пак.
          ...inputBoxStyleOverride(block),
          ...inputTextFontSizeOverride(block),
          ...inputTextColorOverride(block),
        }}
      />
    </div>
  );
}
